/**
 * Phase 10D: Table view of premium history (fallback when chart unavailable or inline).
 * Accepts either data array (for fallback) or policyId (fetches from API).
 */
import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Typography,
} from '@mui/material';
import { renewalsAPI } from '../../services/api';
import { formatCurrency, formatRenewalDate } from '../../utils/renewalHelpers';
import t from '../../utils/renewalsTranslations';

export default function PremiumHistoryTable({ policyId, data: dataProp }) {
  const [rows, setRows] = useState(Array.isArray(dataProp) ? dataProp : []);
  const [loading, setLoading] = useState(!dataProp && !!policyId);

  useEffect(() => {
    if (dataProp) {
      setRows(Array.isArray(dataProp) ? dataProp : []);
      return;
    }
    if (!policyId) return;
    setLoading(true);
    renewalsAPI
      .getPremiumHistory(policyId)
      .then((d) => setRows(Array.isArray(d) ? d : []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [policyId, dataProp]);

  if (loading) return <CircularProgress size={24} />;
  if (rows.length === 0) return <Typography color="text.secondary">{t('renewals.history.noPremiumHistory') || 'No premium history.'}</Typography>;

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{t('renewals.history.date') || 'Date'}</TableCell>
            <TableCell>{t('renewals.history.amount') || 'Amount'}</TableCell>
            <TableCell>{t('renewals.history.change') || 'Change'}</TableCell>
            <TableCell>{t('renewals.history.reason') || 'Reason'}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r, i) => (
            <TableRow key={r.id || i}>
              <TableCell>{formatRenewalDate(r.effective_date)}</TableCell>
              <TableCell>{formatCurrency(r.premium_amount)}</TableCell>
              <TableCell>
                {r.change_amount != null ? formatCurrency(r.change_amount) : '—'}
                {r.change_percentage != null ? ` (${r.change_percentage}%)` : ''}
              </TableCell>
              <TableCell>{r.change_reason ? (t(`renewals.changeReasons.${r.change_reason}`) || r.change_reason) : '—'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
